import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect } from "react";
import routes from "./config";
import { subscribeAuth } from "../services/auth";
import { reload } from "firebase/auth";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const element = useRoutes(routes);

  useEffect(() => {
    if (window.REACT_APP_NAVIGATE) return;
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  }, [navigate]);

  useEffect(() => {
    const allowed = (path: string) => {
      if (path.startsWith('/admin')) return true;
      if (path === '/auth/sign-in') return true;
      if (path === '/auth/sign-up') return true;
      if (path === '/auth/forgot-password') return true;
      if (path === '/auth/verify-reset-code') return true;
      if (path === '/auth/reset-password-code/new') return true;
      if (path === '/auth/email-verification') return true;
      if (path === '/action/verify-email') return true;
      if (path === '/action/reset-password') return true;
      return false;
    };

    const unsub = subscribeAuth(async (user) => {
      if (!user) return;
      // Refresh from server before redirecting; the cached emailVerified flag can lag
      // behind reality (e.g. user verified in another tab/email client).
      try { await reload(user); } catch {}
      if (user.emailVerified) return;
      const currentPath = location.pathname;
      if (allowed(currentPath)) return;
      const redirect = encodeURIComponent(currentPath + location.search);
      navigate(`/auth/email-verification?redirect=${redirect}`, { replace: true });
    });
    return () => unsub();
  }, [location.pathname, location.search, navigate]);
  return element;
}
